






















































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import MmStates from '../../components/MmStates.vue';
import MmStatusBadge from '../../components/MmStatusBadge.vue';
import MmTable from '../../components/MmTable.vue';
import { createCancelSource } from '../../api/mmHttp';
import {
	buildPageQuery,
	createPaginationState,
	createSortingState,
	MmPaginationState,
	MmSortingState,
	resolvePagerTotal,
} from '../../api/pagination';
import { fetchTaskItems } from '../../api/taskItems';
import { formatDiagnostics, isCanceled, MmError, toMmError } from '../../core/errors';
import { formatDateTime, formatMoney, MM_EMPTY_DASH, orDash } from '../../core/format';
import { normalizeSkipReason, resolveOutcome } from '../../core/outcome';
import { skipReasonKey } from '../../core/statuses';
import { MmMonitoringResult, MmTask, MmTaskItem } from '../../types/domain';
import { MmTaskStatus, MmTaskType } from '../../types/enums';
import { listResultColumns } from './taskTypeProfile';

const SECTION_HINT = 'tasks/items';
const KEY_COLUMNS = 'priceMonitoring.mobileMonitoring.taskResults.';

/** Поля в контракте нет, поэтому читаем его по имени ровно в одном месте */
const LAST_RESULT_FIELD = 'lastResult';

const EXPECTED_PRICE_COLUMN = 'expectedPrice';

@Component({
	components: { MmStates, MmStatusBadge, MmTable },
})
export default class TaskItemsTable extends Vue {
	@Prop({ required: true }) readonly task!: MmTask;

	/** Вкладка после первого открытия остаётся смонтированной, поэтому запрос уходит только при isActive */
	@Prop({ default: false }) readonly isActive!: boolean;

	rows: MmTaskItem[] = [];
	pagination: MmPaginationState = createPaginationState();

	sorting: MmSortingState = createSortingState('internalCode', 'asc');

	isLoading: boolean = false;
	loadError: MmError | null = null;

	hasFactColumns: boolean = false;

	private needsReload: boolean = true;

	private listSource: any = null;

	dash: string = MM_EMPTY_DASH;

	mounted(): void {
		if (this.isActive) {
			this.loadItems();
		}
	}

	beforeDestroy(): void {
		this.cancelActiveRequest();
	}

	@Watch('isActive')
	onActiveChanged(next: boolean): void {
		if (next === true && this.needsReload) {
			this.loadItems();
		}
	}

	/** Статус «Назначено» не значит, что результатов нет: они приходят отдельным каналом */
	get isTaskInProgress(): boolean {
		return this.task.status === MmTaskStatus.ASSIGNED && !this.hasFactColumns && !this.isLoading;
	}

	/** Приёмка мониторинга на сервере требует статуса «Завершено» */
	get isAwaitingAuditorClose(): boolean {
		return this.task.status === MmTaskStatus.ASSIGNED && this.hasFactColumns;
	}

	get taskType(): MmTaskType | null {
		return this.task.type ? this.task.type : null;
	}

	get errorDiagnostics(): string {
		return this.loadError === null ? '' : formatDiagnostics(this.loadError.diagnostics);
	}

	/* Метод, а не геттер: заголовки зависят от $t; key без dataIndex — иначе он уйдёт в имя сортировки */

	buildColumns(): any[] {
		const columns: any[] = [
			{
				key: 'internalCode',
				title: this.$t('shared.table.internalCode'),
				width: 170,
				sorter: true,
				sortOrder: this.readSortOrder('internalCode'),
				scopedSlots: { customRender: 'internalCodeCell' },
			},
			{
				key: 'skuName',
				title: this.$t(KEY_COLUMNS + 'colProductName'),
				scopedSlots: { customRender: 'skuNameCell' },
			},
		];

		if (this.hasExpectedPriceColumn) {
			columns.push({
				key: 'expectedPrice',
				title: this.$t(KEY_COLUMNS + 'colExpectedPrice'),
				width: 150,
				scopedSlots: { customRender: 'expectedPriceCell' },
			});
		}

		if (this.hasFactColumns) {
			columns.push({
				key: 'price',
				title: this.$t(KEY_COLUMNS + 'colPrice'),
				width: 140,
				scopedSlots: { customRender: 'priceCell' },
			});
			columns.push({
				key: 'outcome',
				title: this.$t(KEY_COLUMNS + 'colOutcome'),
				width: 230,
				scopedSlots: { customRender: 'outcomeCell' },
			});
			columns.push({
				key: 'capturedAt',
				title: this.$t(KEY_COLUMNS + 'colCapturedAt'),
				width: 170,
				sorter: true,
				sortOrder: this.readSortOrder('capturedAt'),
				scopedSlots: { customRender: 'capturedAtCell' },
			});
			columns.push({
				key: 'auditor',
				title: this.$t(KEY_COLUMNS + 'colAuditor'),
				width: 160,
				scopedSlots: { customRender: 'auditorCell' },
			});
			columns.push({
				key: 'photo',
				title: this.$t(KEY_COLUMNS + 'colPhoto'),
				width: 90,
				scopedSlots: { customRender: 'photoCell' },
			});
		}

		return columns;
	}

	get hasExpectedPriceColumn(): boolean {
		const specs = listResultColumns(this.taskType);

		for (let i = 0; i < specs.length; i += 1) {
			if (specs[i].key === EXPECTED_PRICE_COLUMN) {
				return true;
			}
		}

		return false;
	}

	readSortOrder(field: string): string | undefined {
		if (this.sorting.field !== field || this.sorting.order === null) {
			return undefined;
		}

		return this.sorting.order === 'desc' ? 'descend' : 'ascend';
	}

	/* Читалки терпимы к пустым данным: productKey у позиции-уровня равен null по контракту */

	readInternalCode(item: MmTaskItem): string {
		const key = item && item.productKey ? item.productKey : null;

		return orDash(key ? key.internalCode : null);
	}

	readSkuName(item: MmTaskItem): string {
		return orDash(item ? item.skuName : null);
	}

	/** Сервер может прислать вместо объекта строку-идентификатор, поэтому проверяем тип */
	readLastResult(item: MmTaskItem): MmMonitoringResult | null {
		if (!item) {
			return null;
		}

		const holder = item as { [key: string]: any };
		const raw = holder[LAST_RESULT_FIELD];

		return raw && typeof raw === 'object' ? (raw as MmMonitoringResult) : null;
	}

	/** Скорректированная цена важнее снятой; 0 — настоящая цена, отсутствие цены выражается null */
	readPriceText(item: MmTaskItem): string {
		const result = this.readLastResult(item);

		if (result === null) {
			return MM_EMPTY_DASH;
		}

		const corrected = result.correctedPrice;

		if (corrected !== null && corrected !== undefined) {
			return formatMoney(corrected);
		}

		return formatMoney(result.price);
	}

	resolveRowOutcome(item: MmTaskItem): string {
		return resolveOutcome(this.readLastResult(item), this.taskType);
	}

	readSkipReasonText(item: MmTaskItem): string {
		const result = this.readLastResult(item);
		const code = normalizeSkipReason(result ? result.skipReason : null);

		if (code === '') {
			return '';
		}

		const key = skipReasonKey(code);
		const text = this.$t(key) as string;

		// vue-i18n возвращает сам ключ, когда перевода нет, — это и есть
		// признак незнакомого кода.
		return text === key ? code : text;
	}

	readCapturedAtText(item: MmTaskItem): string {
		const result = this.readLastResult(item);

		return result === null ? MM_EMPTY_DASH : formatDateTime(result.capturedAt);
	}

	readAuditorText(item: MmTaskItem): string {
		const result = this.readLastResult(item);

		return result === null ? MM_EMPTY_DASH : orDash(result.auditorLogin);
	}

	readPhotoCount(item: MmTaskItem): number {
		const result = this.readLastResult(item);

		return result !== null && result.photos ? result.photos.length : 0;
	}

	formatMoney(value: number | null | undefined): string {
		return formatMoney(value);
	}

	handleTableChange(event: { pagination: MmPaginationState; sorter: MmSortingState; filters: any }): void {
		this.pagination = {
			current: event.pagination.current,
			pageSize: event.pagination.pageSize,
			total: this.pagination.total,
		};
		this.sorting = { field: event.sorter.field, order: event.sorter.order };

		this.loadItems();
	}

	reload(): void {
		this.loadItems();
	}

	private loadItems(): void {
		this.cancelActiveRequest();

		const source = createCancelSource();

		this.listSource = source;
		this.isLoading = true;
		this.needsReload = false;

		fetchTaskItems(this.task.id, buildPageQuery(this.pagination, this.sorting), { cancelToken: source.token })
			.then((page) => {
				if (this.listSource !== source) {
					return;
				}

				this.rows = page && page.content ? page.content : [];
				this.pagination = {
					current: this.pagination.current,
					pageSize: this.pagination.pageSize,
					total: page ? resolvePagerTotal(page, this.pagination) : 0,
				};
				this.loadError = null;
				this.rememberFactSupport(this.rows);
				this.$emit('items-total', this.pagination.total);
			})
			.catch((error) => {
				if (isCanceled(error) || this.listSource !== source) {
					return;
				}

				this.rows = [];
				this.pagination.total = 0;
				this.loadError = toMmError(error, SECTION_HINT);
			})
			.finally(() => {
				if (this.listSource !== source) {
					return;
				}

				this.listSource = null;
				this.isLoading = false;
			});
	}

	private rememberFactSupport(rows: MmTaskItem[]): void {
		if (this.hasFactColumns) {
			return;
		}

		for (let i = 0; i < rows.length; i += 1) {
			if (this.readLastResult(rows[i]) !== null) {
				this.hasFactColumns = true;

				return;
			}
		}
	}

	private cancelActiveRequest(): void {
		if (this.listSource !== null) {
			this.listSource.cancel();
			this.listSource = null;
		}
	}
}
